import { memo } from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { AbandonedCartPaymentAttemptDto } from '@tiendanube/common';
import Collapsible from 'commons/components/Collapsible';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { PreviousPaymentAttemptItem } from './PreviousPaymentAttemptItem';

interface PreviousPaymentAttemptsProps {
  attempts: AbandonedCartPaymentAttemptDto[];
  paymentCount?: number;
}

function PreviousPaymentAttemptsComponent({
  attempts,
  paymentCount,
}: Readonly<PreviousPaymentAttemptsProps>) {
  const t = useTranslationOrders('abandonedCarts.payments');

  const previousAttempts = attempts.slice(1);

  const totalCount = Math.max(paymentCount ?? 0, attempts.length);
  const previousCount = totalCount > 0 ? totalCount - 1 : 0;
  const unidentifiedCount = Math.max(0, (paymentCount ?? 0) - attempts.length);

  if (previousCount <= 0) {
    return null;
  }

  const unidentifiedAttemptsText: () => JSX.Element = () => (
    <Text fontSize="base" color="neutral-textLow">
      {t('unidentifiedAttempts', { count: unidentifiedCount })}
    </Text>
  );

  if (previousAttempts.length === 0 && unidentifiedCount > 0) {
    return <Box marginTop="2">{unidentifiedAttemptsText()}</Box>;
  }

  return (
    <Box marginTop="2">
      <Collapsible
        header={
          <Text fontSize="base" color="primary-interactive">
            {t('previousAttempts', { count: previousCount })}
          </Text>
        }
        defaultOpen={false}
      >
        <Box display="flex" flexDirection="column" gap="4" marginTop="2">
          {previousAttempts.map((attempt, index) => (
            <Box key={attempt.id}>
              {index > 0 && (
                <Box
                  width="100%"
                  height="1px"
                  backgroundColor="neutral-surfaceHighlight"
                  marginBottom="4"
                />
              )}
              <PreviousPaymentAttemptItem attempt={attempt} />
            </Box>
          ))}
          {unidentifiedCount > 0 && (
            <>
              {previousAttempts.length > 0 && (
                <Box
                  width="100%"
                  height="1px"
                  backgroundColor="neutral-surfaceHighlight"
                  marginTop="4"
                />
              )}
              {unidentifiedAttemptsText()}
            </>
          )}
        </Box>
      </Collapsible>
    </Box>
  );
}

export const PreviousPaymentAttempts = memo(PreviousPaymentAttemptsComponent);
